/**
 * NeuerTerminButton — globaler Kopfzeilen-Button mit Aufklapp-Formular (Megamenü-Stil)
 * zum schnellen Anlegen eines Termins von überall. Eigenständig (kein Eingriff in KalenderPage).
 * Features: Titel, Beschreibung, Betrieb, Status, Kategorie (je Betrieb), Start/Ende, Ort,
 * Akte (suchen/verknüpfen ODER neue Akte im Betrieb anlegen mit Rückfrage), Kunde, Erinnerung.
 */
import { useState, useRef, useEffect } from 'react';
import { Plus, X, CalendarPlus, MapPin, Link as LinkIcon, Users, FilePlus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { BETRIEB_LIST, BETRIEBE, type BetriebId } from '@/lib/betriebe';
import { kategorienFuerBetrieb } from '@/lib/calendarCategories';
import { DateTimePicker } from '@/components/ui/DateTimePicker';

const STATUS_OPTS: [string, string][] = [
  ['geplant', 'Geplant'], ['bestaetigt', 'Bestätigt'], ['erledigt', 'Erledigt'], ['abgesagt', 'Abgesagt'],
];
const ERINNERUNG_OPTS: [number, string][] = [
  [0, 'Keine Erinnerung'], [10, '10 Min vorher'], [30, '30 Min vorher'], [60, '1 Std vorher'], [1440, '1 Tag vorher'],
];
const AKTE_PREFIX: Record<string, string> = {
  gutachten: 'GA', gutachter_koop: 'GA', autoglas: 'AG', mietwagen: 'MW',
  werkstatt: 'WK', protex: 'PX', unfallgiganten: 'UG', airbnb: 'AB', recht: 'RA',
};

function toISOLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function genAktenNr(): string {
  const year = new Date().getFullYear();
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return `GK-${year}-${String(buf[0] % 1000000).padStart(6, '0')}`;
}
function aktenNummerFor(betrieb: string, nr: string): string {
  const prefix = AKTE_PREFIX[betrieb] || 'XX';
  const parts = nr.split('-');
  const suffix = parts.length >= 3 ? parts.slice(1).join('-') : nr;
  return `${prefix}-${suffix}`;
}
function escapeIlike(s: string): string {
  return s.replace(/([\\%_,()])/g, '\\$1');
}

export default function NeuerTerminButton() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [betrieb, setBetrieb] = useState('gutachten');
  const [status, setStatus] = useState('geplant');
  const [kategorie, setKategorie] = useState('');
  const [ort, setOrt] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [erinnerung, setErinnerung] = useState(0);

  const [akteId, setAkteId] = useState<string | null>(null);
  const [akteLabel, setAkteLabel] = useState<string | null>(null);
  const [neueAkte, setNeueAkte] = useState(false);
  const [akteQuery, setAkteQuery] = useState('');
  const [akteResults, setAkteResults] = useState<{ id: string; label: string }[]>([]);

  const [kundeId, setKundeId] = useState<string | null>(null);
  const [kundeLabel, setKundeLabel] = useState<string | null>(null);
  const [kundeQuery, setKundeQuery] = useState('');
  const [kundeResults, setKundeResults] = useState<{ id: string; label: string }[]>([]);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    now.setMinutes(now.getMinutes() < 30 ? 0 : 30, 0, 0);
    const e = new Date(now);
    e.setHours(e.getHours() + 1);
    setTitel(''); setBeschreibung(''); setBetrieb('gutachten'); setStatus('geplant');
    setKategorie(''); setOrt(''); setStart(toISOLocal(now)); setEnd(toISOLocal(e));
    setErinnerung(0); setAkteId(null); setAkteLabel(null); setNeueAkte(false);
    setAkteQuery(''); setAkteResults([]); setKundeId(null); setKundeLabel(null);
    setKundeQuery(''); setKundeResults([]); setSaving(false);
  }, [open]);

  useEffect(() => {
    function onDown(ev: MouseEvent) {
      const t = ev.target as Element;
      if (ref.current && !ref.current.contains(t)) {
        if (t.closest && t.closest('[data-radix-popper-content-wrapper]')) return;
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    const q = akteQuery.trim();
    if (q.length < 2 || neueAkte) { setAkteResults([]); return; }
    let cancel = false;
    const h = setTimeout(async () => {
      const like = `%${escapeIlike(q)}%`;
      const { data } = await supabase.from('akten')
        .select('id, akten_nummer, kennzeichen, kunde_name')
        .or(`akten_nummer.ilike.${like},kennzeichen.ilike.${like},kunde_name.ilike.${like}`)
        .limit(6);
      if (cancel) return;
      setAkteResults((data || []).map((a: { id: string; akten_nummer?: string; kennzeichen?: string; kunde_name?: string }) => ({
        id: a.id,
        label: [a.akten_nummer, a.kennzeichen, a.kunde_name].filter(Boolean).join(' · ') || 'Akte',
      })));
    }, 250);
    return () => { cancel = true; clearTimeout(h); };
  }, [akteQuery, neueAkte]);

  useEffect(() => {
    const q = kundeQuery.trim();
    if (q.length < 2) { setKundeResults([]); return; }
    let cancel = false;
    const h = setTimeout(async () => {
      const like = `%${escapeIlike(q)}%`;
      const { data } = await supabase.from('kunden')
        .select('id, vorname, nachname, firma')
        .or(`vorname.ilike.${like},nachname.ilike.${like},firma.ilike.${like}`)
        .limit(6);
      if (cancel) return;
      setKundeResults((data || []).map((k: { id: string; vorname?: string; nachname?: string; firma?: string }) => ({
        id: k.id,
        label: k.firma || [k.vorname, k.nachname].filter(Boolean).join(' ') || 'Kunde',
      })));
    }, 250);
    return () => { cancel = true; clearTimeout(h); };
  }, [kundeQuery]);

  async function createQuickAkte(): Promise<string | null | undefined> {
    const name = BETRIEBE[betrieb as BetriebId]?.name || betrieb;
    if (!window.confirm(`Es wird eine neue Akte in „${name}" erstellt. Fortfahren?`)) return undefined;
    for (let i = 0; i < 3; i++) {
      const nr = genAktenNr();
      const nummer = aktenNummerFor(betrieb, nr);
      const { data, error } = await supabase.from('akten').insert({
        akten_nr: nr, akten_nummer: nummer, business_unit: betrieb,
        titel: titel.trim() || 'Neue Akte', status: 'neu', prioritaet: 'mittel',
        kunde_name: kundeLabel || '', created_via: 'manuell',
      }).select('id, akten_nummer').single();
      if (!error && data) {
        toast.success(`Neue Akte ${data.akten_nummer} angelegt`);
        return data.id as string;
      }
      if (error && (error as { code?: string }).code !== '23505') {
        console.error('Akte-Anlage fehlgeschlagen:', error);
        toast.error('Neue Akte konnte nicht angelegt werden');
        return null;
      }
    }
    toast.error('Neue Akte konnte nicht angelegt werden');
    return null;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!titel.trim() || !start || !end) return;
    setSaving(true);
    try {
      let finalAkteId = akteId;
      if (neueAkte) {
        const id = await createQuickAkte();
        if (!id) { setSaving(false); return; }
        finalAkteId = id;
      }
      const { error } = await supabase.from('termine').insert({
        titel: titel.trim(),
        beschreibung: beschreibung || null,
        ort: ort || null,
        beginn: new Date(start).toISOString(),
        ende: new Date(end).toISOString(),
        business_unit: betrieb,
        status,
        kategorie: kategorie || null,
        akte_id: finalAkteId || null,
        kunde_id: kundeId || null,
        erinnerung_minuten: erinnerung || null,
        ganztaegig: false,
      });
      if (error) throw error;
      toast.success('Termin erstellt');
      setOpen(false);
    } catch (err) {
      toast.error(`Termin konnte nicht erstellt werden: ${(err as Error)?.message || 'Fehler'}`);
    } finally {
      setSaving(false);
    }
  }

  const inputCls = 'w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all';
  const lblCls = 'block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-600 min-h-[36px]"
        title="Neuer Termin"
      >
        <CalendarPlus className="w-4 h-4" />
        <span className="hidden lg:inline">Neuer Termin</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[calc(100vw-1rem)] sm:w-[420px] max-h-[82vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-100 z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(196,30,58,0.08)' }}>
                <Plus className="w-3.5 h-3.5" style={{ color: '#C41E3A' }} />
              </div>
              <h3 className="text-sm font-bold text-gray-900">Neuer Termin</h3>
            </div>
            <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3">
            <div>
              <label className={lblCls}>Titel *</label>
              <input type="text" value={titel} onChange={e => setTitel(e.target.value)} required placeholder="z.B. Besichtigung VW Golf" className={inputCls} />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div><label className={lblCls}>Start</label><DateTimePicker value={start} onChange={v => setStart(v ?? '')} /></div>
              <div><label className={lblCls}>Ende</label><DateTimePicker value={end} onChange={v => setEnd(v ?? '')} /></div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={lblCls}>Betrieb</label>
                <select value={betrieb} onChange={e => { setBetrieb(e.target.value); setKategorie(''); }} className={`${inputCls} bg-white`}>
                  {BETRIEB_LIST.filter(b => b.hasData).map(b => <option key={b.id} value={b.id}>{BETRIEBE[b.id as BetriebId]?.name || b.id}</option>)}
                </select>
              </div>
              <div>
                <label className={lblCls}>Status</label>
                <select value={status} onChange={e => setStatus(e.target.value)} className={`${inputCls} bg-white`}>
                  {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={lblCls}>Was ist zu tun? (Kategorie)</label>
              <select value={kategorie} onChange={e => setKategorie(e.target.value)} className={`${inputCls} bg-white`}>
                <option value="">Keine Kategorie</option>
                {kategorienFuerBetrieb(betrieb).map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>

            <div>
              <label className={lblCls}>Ort</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
                <input type="text" value={ort} onChange={e => setOrt(e.target.value)} placeholder="Adresse oder Ort" className={`${inputCls} pl-9`} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={`${lblCls} mb-0`}>Akte</label>
                <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
                  <input type="checkbox" checked={neueAkte} onChange={e => { setNeueAkte(e.target.checked); setAkteId(null); setAkteLabel(null); setAkteQuery(''); }} />
                  Neue Akte erstellen
                </label>
              </div>
              {neueAkte ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-200 bg-amber-50 text-xs text-amber-700">
                  <FilePlus className="w-3.5 h-3.5 flex-shrink-0" /> Neue Akte wird in „{BETRIEBE[betrieb as BetriebId]?.name || betrieb}" erstellt
                </div>
              ) : akteId ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
                  <LinkIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 flex-1 truncate">{akteLabel}</span>
                  <button type="button" onClick={() => { setAkteId(null); setAkteLabel(null); setAkteQuery(''); }} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div className="relative">
                  <input type="text" value={akteQuery} onChange={e => setAkteQuery(e.target.value)} placeholder="Aktennr., Kennzeichen oder Kunde suchen…" className={inputCls} />
                  {akteQuery.trim().length >= 2 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-44 overflow-y-auto">
                      {akteResults.length === 0
                        ? <div className="px-3 py-2 text-xs text-gray-400">Keine Akte gefunden</div>
                        : akteResults.map(r => <button key={r.id} type="button" onClick={() => { setAkteId(r.id); setAkteLabel(r.label); setAkteQuery(''); }} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 truncate">{r.label}</button>)}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className={lblCls}>Kunde (optional)</label>
              {kundeId ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
                  <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 flex-1 truncate">{kundeLabel}</span>
                  <button type="button" onClick={() => { setKundeId(null); setKundeLabel(null); setKundeQuery(''); }} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div className="relative">
                  <input type="text" value={kundeQuery} onChange={e => setKundeQuery(e.target.value)} placeholder="Kunde (Name oder Firma) suchen…" className={inputCls} />
                  {kundeQuery.trim().length >= 2 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-44 overflow-y-auto">
                      {kundeResults.length === 0
                        ? <div className="px-3 py-2 text-xs text-gray-400">Kein Kunde gefunden</div>
                        : kundeResults.map(r => <button key={r.id} type="button" onClick={() => { setKundeId(r.id); setKundeLabel(r.label); setKundeQuery(''); }} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 truncate">{r.label}</button>)}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className={lblCls}>Erinnerung</label>
              <select value={erinnerung} onChange={e => setErinnerung(Number(e.target.value))} className={`${inputCls} bg-white`}>
                {ERINNERUNG_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            <div>
              <label className={lblCls}>Beschreibung</label>
              <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)} placeholder="Was muss gemacht werden? Notizen…" rows={2} className={`${inputCls} resize-none`} />
            </div>

            <div className="flex gap-2 pt-1 pb-1">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50">Abbrechen</button>
              <button type="submit" disabled={saving || !titel.trim() || !start || !end} className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={{ backgroundColor: '#C41E3A' }}>
                {saving ? 'Speichern…' : 'Termin erstellen'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
